import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import Icon from '../AppIcon';

/**
 * Promise-based confirmation dialog.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: 'Delete this listing?',
 *     description: 'This cannot be undone.',
 *     confirmLabel: 'Delete',
 *     destructive: true,
 *   });
 *   if (!ok) return;
 *
 * Wrap the app once with <ConfirmProvider>.
 */
const ConfirmContext = createContext(null);

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within <ConfirmProvider>');
  return ctx;
};

export const ConfirmProvider = ({ children }) => {
  const [state, setState] = useState(null); // { title, description, confirmLabel, cancelLabel, destructive }
  const resolverRef = useRef(null);

  const ask = useCallback((opts = {}) => {
    setState({
      title: opts.title || 'Are you sure?',
      description: opts.description || '',
      confirmLabel: opts.confirmLabel || 'Confirm',
      cancelLabel: opts.cancelLabel || 'Cancel',
      destructive: !!opts.destructive,
    });
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const close = useCallback((result) => {
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
    setState(null);
  }, []);

  // ESC closes the dialog with a "cancel" result. Only bound while open
  // so we don't hold a global listener for nothing.
  useEffect(() => {
    if (!state) return;
    const onKey = (e) => { if (e.key === 'Escape') close(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [state, close]);

  // Lock background scroll while open
  useEffect(() => {
    if (!state) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [state]);

  return (
    <ConfirmContext.Provider value={ask}>
      {children}
      {state && (
        <div
          className="fixed inset-0 z-[1100] flex items-center justify-center px-4 bg-black/50 animate-in fade-in duration-150"
          onClick={() => close(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-title"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm bg-white rounded-2xl shadow-[0_24px_48px_-12px_rgba(17,24,39,0.25)] p-6 animate-in zoom-in-95 duration-200"
          >
            <div className="flex items-start gap-3 mb-4">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                  state.destructive ? 'bg-rose-50 text-rose-500' : 'bg-gray-100 text-gray-700'
                }`}
              >
                <Icon name={state.destructive ? 'AlertTriangle' : 'HelpCircle'} size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <h3 id="confirm-title" className="text-base font-semibold text-gray-900">
                  {state.title}
                </h3>
                {state.description && (
                  <p className="text-sm text-gray-600 mt-1">{state.description}</p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 mt-6">
              <button
                onClick={() => close(false)}
                className="px-4 py-2 rounded-full text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
              >
                {state.cancelLabel}
              </button>
              <button
                onClick={() => close(true)}
                autoFocus
                className={`px-4 py-2 rounded-full text-sm font-semibold text-white transition shadow-[0_2px_8px_rgba(244,63,94,0.25)] ${
                  state.destructive
                    ? 'bg-rose-500 hover:bg-rose-600'
                    : 'bg-gray-900 hover:bg-black shadow-none'
                }`}
              >
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};
